import { Link } from 'react-router-dom';
import { Users, Building2, Truck, LogIn, Shield, Clock } from 'lucide-react';

function Portal() {
  // Ironclad Employee Portal URL
  const IRONCLAD_URL = "https://ironclad.williamsdiversified.com/";

  const portalOptions = [
    {
      icon: <Users className="w-16 h-16" />,
      title: 'Employee Portal',
      description: 'Access schedules, timesheets, project assignments, and internal resources.',
      buttonText: 'Employee Login',
      url: IRONCLAD_URL
    },
    {
      icon: <Building2 className="w-16 h-16" />,
      title: 'Client Portal',
      description: 'View project status, invoices, documents, and communicate with your project team.',
      buttonText: 'Client Login',
      url: IRONCLAD_URL
    },
    {
      icon: <Truck className="w-16 h-16" />,
      title: 'Vendor Portal',
      description: 'Submit bids, manage contracts, upload certifications, and track payments.',
      buttonText: 'Vendor Login',
      url: IRONCLAD_URL
    }
  ];

  return (
    <div className="pt-28">
      {/* Hero Section */}
      <section className="bg-black py-24 relative overflow-hidden">
        <div className="absolute inset-0 brass-lighting"></div>
        <div className="absolute inset-0 geometric-brass"></div>
        <div className="absolute inset-0 shimmer-wave"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="flex items-center justify-center mb-6">
            <LogIn className="w-20 h-20 text-gold" />
          </div>
          <span className="text-gold text-sm font-semibold tracking-[0.3em] uppercase mb-4 block">Secure Access</span>
          <div className="w-24 h-1 bg-gold mx-auto mb-8"></div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            Command <span className="text-gold">Center</span>
          </h1>
          <p className="text-gray-300 text-xl max-w-3xl mx-auto">
            Your centralized hub for project management, communications, and resources. 
            Select your portal below to access your personalized dashboard.
          </p>
        </div>
      </section>

      {/* Portal Options */}
      <section className="bg-black py-24 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting"></div>
        <div className="absolute inset-0 geometric-brass"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {portalOptions.map((portal, index) => (
              <div 
                key={index}
                className="relative overflow-hidden rounded-lg border-2 border-gold/30 hover:border-gold transition-all group hover:transform hover:scale-105 hover:shadow-2xl bg-transparent"
              >
                <div className="p-10 text-center">
                  <div className="text-gold mb-6 flex justify-center group-hover:scale-110 transition-transform">
                    {portal.icon}
                  </div>
                  <h3 className="text-white font-bold text-2xl mb-4 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                    {portal.title}
                  </h3>
                  <p className="text-gray-400 mb-8 leading-relaxed">
                    {portal.description}
                  </p>
                  <a
                    href={portal.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block bg-gold hover:bg-gold-dark text-black font-bold py-4 px-8 rounded text-base transition-all transform hover:scale-105 uppercase tracking-wider w-full"
                  >
                    {portal.buttonText}
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="bg-black py-24 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting"></div>
        <div className="absolute inset-0 geometric-brass"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
              Portal <span className="text-gold">Features</span>
            </h2>
            <div className="w-24 h-1 bg-gold mx-auto mb-8"></div>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-gold" />
              </div>
              <h4 className="text-white font-bold text-xl mb-2">Secure Access</h4>
              <p className="text-gray-400">Enterprise-grade security with encrypted connections</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-gold" />
              </div>
              <h4 className="text-white font-bold text-xl mb-2">24/7 Availability</h4>
              <p className="text-gray-400">Access your dashboard anytime, anywhere</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Building2 className="w-8 h-8 text-gold" />
              </div>
              <h4 className="text-white font-bold text-xl mb-2">Real-Time Updates</h4>
              <p className="text-gray-400">Live project tracking and instant notifications</p>
            </div>
          </div>
        </div>
      </section>

      {/* Help Section */}
      <section className="bg-black py-24 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting"></div>
        <div className="absolute inset-0 geometric-brass"></div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h3 className="text-4xl md:text-5xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            Need <span className="text-gold">Assistance?</span>
          </h3>
          <p className="text-gray-300 text-xl mb-10">
            Having trouble accessing your portal? Our team is here to help.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a 
              href="tel:9184933418"
              className="inline-block bg-gold hover:bg-gold-dark text-black font-bold py-4 px-10 rounded text-lg transition-all transform hover:scale-105 uppercase tracking-wider"
            >
              Call: (918) 493-3418
            </a>
            <a 
              href="mailto:inquire@williamsdiversified.com"
              className="inline-block bg-transparent border-2 border-gold hover:bg-gold hover:text-black text-gold font-bold py-4 px-10 rounded text-lg transition-all transform hover:scale-105 uppercase tracking-wider"
            >
              Email Support
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Portal;
